/**
 * OffersTable Component
 *
 * Searchable, paginated table of offers fetched from the LP API.
 * Includes filter bar (offer type, currency, status) and a detail
 * modal triggered per row.
 *
 * @component
 */

import React, { useState } from "react";
import { ITEMS_PER_PAGE } from "./constants";
import { commonStyles, colors, paginationStyles, buttonStyles } from "./styles";
import {
  formatDate,
  formatAmount,
  truncateText,
  getOfferStatus,
  getStatusBadgeStyle,
  getOfferTypeBadgeStyle,
  calculatePaginationRange,
} from "./utils";
import OfferDetail from "./OfferDetail";

// ─── Pagination ──────────────────────────────────────────────────────────────

const Pagination = ({ currentPage, totalPages, onPageChange, totalItems }) => {
  if (totalPages <= 1) return null;

  const pages = calculatePaginationRange(currentPage, totalPages);
  const startItem = (currentPage - 1) * ITEMS_PER_PAGE + 1;
  const endItem = Math.min(currentPage * ITEMS_PER_PAGE, totalItems);

  return (
    <div style={paginationStyles.container}>
      <span style={paginationStyles.info}>
        Showing {startItem}, {endItem} of {totalItems} offers
      </span>
      <div style={paginationStyles.nav}>
        <button
          type="button"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          style={{
            ...paginationStyles.navButton,
            background: currentPage === 1 ? colors.gray300 : colors.primary,
            color: currentPage === 1 ? colors.gray500 : colors.white,
            cursor: currentPage === 1 ? "not-allowed" : "pointer",
          }}
        >
          ← Prev
        </button>
        {pages.map((page, i) =>
          page === "..." ? (
            <span
              key={`ellipsis-${i}`}
              style={{ padding: "0 6px", color: colors.gray500 }}
            >
              …
            </span>
          ) : (
            <button
              key={page}
              type="button"
              onClick={() => onPageChange(page)}
              style={{
                ...paginationStyles.pageButton,
                background: page === currentPage ? colors.accent : colors.white,
                color: page === currentPage ? colors.white : colors.gray700,
                border:
                  page === currentPage ? "none" : `1px solid ${colors.border}`,
                fontWeight: page === currentPage ? 700 : 400,
              }}
            >
              {page}
            </button>
          ),
        )}
        <button
          type="button"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          style={{
            ...paginationStyles.navButton,
            background:
              currentPage === totalPages ? colors.gray300 : colors.primary,
            color: currentPage === totalPages ? colors.gray500 : colors.white,
            cursor: currentPage === totalPages ? "not-allowed" : "pointer",
          }}
        >
          Next →
        </button>
      </div>
    </div>
  );
};

// ─── Filter Bar ──────────────────────────────────────────────────────────────

const FilterBar = ({
  searchQuery,
  onSearchChange,
  offerTypeFilter,
  onOfferTypeChange,
  currencyFilter,
  onCurrencyChange,
  statusFilter,
  onStatusChange,
  currencies,
  totalShowing,
  totalFiltered,
  loading,
}) => {
  const selectStyle = {
    padding: "8px 12px",
    paddingRight: "32px",
    border: `1px solid ${colors.gray400}`,
    borderRadius: "4px",
    fontSize: "13px",
    background: colors.white,
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23333' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 10px center",
    backgroundSize: "12px",
    cursor: "pointer",
    appearance: "none",
    WebkitAppearance: "none",
    minWidth: "130px",
  };

  return (
    <div style={commonStyles.filterBar}>
      {/* Search input */}
      <div
        style={{
          display: "flex",
          gap: "8px",
          flex: 1,
          maxWidth: "400px",
          alignItems: "center",
        }}
      >
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search by email, currency…"
          style={{ ...commonStyles.input, flex: 1 }}
        />
        {searchQuery && (
          <button
            type="button"
            onClick={() => onSearchChange("")}
            title="Clear search"
            style={{
              width: "36px",
              height: "36px",
              padding: 0,
              background: colors.gray300,
              color: colors.gray700,
              border: "none",
              borderRadius: "4px",
              fontSize: "16px",
              fontWeight: 700,
              lineHeight: 1,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            ✕
          </button>
        )}
      </div>

      {/* Dropdowns */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          flexWrap: "wrap",
        }}
      >
        <select
          value={offerTypeFilter}
          onChange={(e) => onOfferTypeChange(e.target.value)}
          style={selectStyle}
        >
          <option value="all">All Types</option>
          <option value="long">Long</option>
          <option value="short">Short</option>
        </select>

        <select
          value={statusFilter}
          onChange={(e) => onStatusChange(e.target.value)}
          style={selectStyle}
        >
          <option value="all">All Statuses</option>
          <option value="live">Live</option>
          <option value="expired">Expired</option>
          <option value="settled">Settled</option>
          <option value="delisted">Delisted</option>
          <option value="deleted">Deleted</option>
          <option value="cutoff_passed">Cutoff Passed</option>
        </select>

        <select
          value={currencyFilter}
          onChange={(e) => onCurrencyChange(e.target.value)}
          style={selectStyle}
        >
          <option value="all">All Currencies</option>
          {currencies.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>

        <span
          style={{
            ...commonStyles.badge,
            background: colors.accent,
            color: colors.white,
          }}
        >
          {loading ? "Loading…" : `${totalShowing} of ${totalFiltered}`}
        </span>
      </div>
    </div>
  );
};

// ─── Stats Bar ────────────────────────────────────────────────────────────────

const StatsBar = ({ offers }) => {
  const live = offers.filter((o) => getOfferStatus(o) === "Live").length;
  const expired = offers.filter((o) => getOfferStatus(o) === "Expired").length;
  const settled = offers.filter((o) => getOfferStatus(o) === "Settled").length;
  const delisted = offers.filter(
    (o) => getOfferStatus(o) === "Delisted",
  ).length;
  const deleted = offers.filter((o) => getOfferStatus(o) === "Deleted").length;
  const cutoffPassed = offers.filter(
    (o) => getOfferStatus(o) === "Cutoff Passed",
  ).length;
  const long = offers.filter((o) => o.offerType === "long").length;
  const short = offers.filter((o) => o.offerType === "short").length;

  const cards = [
    {
      label: "Total",
      value: offers.length,
      gradient: "linear-gradient(135deg, #2e3b61 0%, #3d4d7a 100%)",
      tooltip: "Total number of prediction offers created on the platform",
    },
    {
      label: "Live",
      value: live,
      gradient: "linear-gradient(135deg, #28a745 0%, #218838 100%)",
      tooltip: "Offers currently active and accepting trader engagement",
    },
    {
      label: "Expired",
      value: expired,
      gradient: "linear-gradient(135deg, #dc3545 0%, #c82333 100%)",
      tooltip: "Offers whose duration has ended but have not yet been settled",
    },
    {
      label: "Settled",
      value: settled,
      gradient: "linear-gradient(135deg, #ffc107 0%, #e0a800 100%)",
      tooltip:
        "Offers that have been fully settled, winners paid out, losers debited",
    },
    {
      label: "Delisted",
      value: delisted,
      gradient: "linear-gradient(135deg, #e67e22 0%, #d35400 100%)",
      tooltip:
        "Offers removed from the marketplace by admin for T&C violations",
    },
    {
      label: "Deleted",
      value: deleted,
      gradient: "linear-gradient(135deg, #7f1d1d 0%, #991b1b 100%)",
      tooltip:
        "Offers permanently soft-deleted by admin, escrow released, owner notified",
    },
    {
      label: "Cutoff Passed",
      value: cutoffPassed,
      gradient: "linear-gradient(135deg, #0dcaf0 0%, #0aa2c0 100%)",
      tooltip:
        "Offers past their engagement deadline, no new trades allowed, waiting for expiry",
    },
    {
      label: "Long",
      value: long,
      gradient: "linear-gradient(135deg, #17a2b8 0%, #138496 100%)",
      tooltip: "Offers where the creator predicted the rate will go UP",
    },
    {
      label: "Short",
      value: short,
      gradient: "linear-gradient(135deg, #6c3483 0%, #5b2c6f 100%)",
      tooltip: "Offers where the creator predicted the rate will go DOWN",
    },
  ];

  return (
    <div
      style={{
        display: "flex",
        gap: "12px",
        marginBottom: "20px",
        flexWrap: "wrap",
      }}
    >
      {cards.map((card) => (
        <div
          key={card.label}
          title={card.tooltip}
          style={{
            flex: 1,
            minWidth: "100px",
            padding: "14px 16px",
            borderRadius: "6px",
            textAlign: "center",
            background: card.gradient,
            cursor: "help",
            transition: "transform 0.15s ease, box-shadow 0.15s ease",
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.2)";
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "none";
          }}
        >
          <span
            style={{
              display: "block",
              fontSize: "26px",
              fontWeight: 700,
              color: colors.white,
            }}
          >
            {card.value}
          </span>
          <span
            style={{
              fontSize: "11px",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.85)",
              fontWeight: 600,
              letterSpacing: "0.5px",
            }}
          >
            {card.label}
          </span>
        </div>
      ))}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const OffersTable = ({
  offers,
  allOffers,
  loading,
  totalCount,
  currentPage,
  totalPages,
  searchQuery,
  onSearchChange,
  offerTypeFilter,
  onOfferTypeChange,
  currencyFilter,
  onCurrencyChange,
  statusFilter,
  onStatusChange,
  onPageChange,
  currencies,
  paginatedOffers,
  onRefresh,
}) => {
  const [selectedOffer, setSelectedOffer] = useState(null);

  // After an admin action, refresh data and close the modal
  const handleAdminActionComplete = () => {
    setSelectedOffer(null);
    if (onRefresh) onRefresh();
  };

  // Loading spinner (initial load only)
  if (loading && offers.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "60px 20px" }}>
        <div
          style={{
            width: "40px",
            height: "40px",
            border: `3px solid ${colors.border}`,
            borderTopColor: colors.primary,
            borderRadius: "50%",
            animation: "spin 0.8s linear infinite",
            margin: "0 auto 16px",
          }}
        />
        <p style={{ color: colors.gray600, fontSize: "14px" }}>
          Loading offers…
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Section Header */}
      <div style={commonStyles.sectionHeader}>
        <h2
          style={{
            margin: 0,
            color: colors.white,
            fontSize: "20px",
            fontWeight: 600,
          }}
        >
          All Prediction Offers
        </h2>
        <span
          style={{
            background: "rgba(255,255,255,0.15)",
            color: colors.white,
            padding: "6px 14px",
            borderRadius: "20px",
            fontSize: "13px",
            fontWeight: 600,
          }}
        >
          Total: {totalCount} Offer{totalCount !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Stats */}
      <StatsBar offers={allOffers} />

      {/* Filters */}
      <FilterBar
        searchQuery={searchQuery}
        onSearchChange={onSearchChange}
        offerTypeFilter={offerTypeFilter}
        onOfferTypeChange={onOfferTypeChange}
        currencyFilter={currencyFilter}
        onCurrencyChange={onCurrencyChange}
        statusFilter={statusFilter}
        onStatusChange={onStatusChange}
        currencies={currencies}
        totalShowing={paginatedOffers.length}
        totalFiltered={offers.length}
        loading={loading}
      />

      {/* Empty state */}
      {offers.length === 0 && !loading ? (
        <div className="blank_slate">
          <p>
            {searchQuery ||
            offerTypeFilter !== "all" ||
            currencyFilter !== "all" ||
            statusFilter !== "all"
              ? "No offers match your filters"
              : "No offers found"}
          </p>
        </div>
      ) : (
        <div style={{ ...commonStyles.panel, marginBottom: 0 }}>
          <div className="index_table_container">
            <table className="index_table">
              <thead>
                <tr>
                  <th className="col">Offer ID</th>
                  <th className="col">Creator Email</th>
                  <th className="col">Type</th>
                  <th className="col">Currency</th>
                  <th className="col">Amount</th>
                  <th className="col">Leverage</th>
                  <th className="col">Rate</th>
                  <th className="col">Traders</th>
                  <th className="col">Total Staked</th>
                  <th className="col">Status</th>
                  <th className="col">Created At</th>
                  <th className="col">Expires At</th>
                  <th className="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedOffers.map((offer, idx) => {
                  const status = getOfferStatus(offer);
                  const statusStyle = getStatusBadgeStyle(status);
                  const typeStyle = getOfferTypeBadgeStyle(offer.offerType);

                  return (
                    <tr
                      key={offer.id || idx}
                      className={idx % 2 === 0 ? "even" : "odd"}
                    >
                      <td className="col">
                        <code style={{ fontSize: "11px" }} title={offer.id}>
                          {truncateText(offer.id, 12)}
                        </code>
                      </td>
                      <td className="col">{offer.userEmail || "-"}</td>
                      <td className="col">
                        <span style={typeStyle}>{offer.offerType}</span>
                      </td>
                      <td className="col">{offer.currency || "-"}</td>
                      <td className="col">
                        <strong>{formatAmount(offer.totalAmount)} USDb</strong>
                      </td>
                      <td className="col">{offer.leverage || "-"}</td>
                      <td className="col">{offer.exchangeRate || "-"}</td>
                      <td className="col" style={{ textAlign: "center" }}>
                        {offer.traderCount ?? 0}
                      </td>
                      <td className="col">
                        {formatAmount(offer.totalStaked)} USDb
                      </td>
                      <td className="col">
                        <span style={statusStyle}>{status}</span>
                      </td>
                      <td className="col">{formatDate(offer.createdAt)}</td>
                      <td className="col">{formatDate(offer.expiresAt)}</td>
                      <td className="col">
                        <button
                          type="button"
                          onClick={() => setSelectedOffer(offer)}
                          style={buttonStyles.viewDetails}
                          onMouseOver={(e) =>
                            Object.assign(
                              e.currentTarget.style,
                              buttonStyles.viewDetailsHover,
                            )
                          }
                          onMouseOut={(e) =>
                            Object.assign(
                              e.currentTarget.style,
                              buttonStyles.viewDetailsDefault,
                            )
                          }
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
            totalItems={offers.length}
          />
        </div>
      )}

      {/* Detail Modal */}
      {selectedOffer && (
        <OfferDetail
          offer={selectedOffer}
          onClose={() => setSelectedOffer(null)}
          onActionComplete={handleAdminActionComplete}
        />
      )}
    </div>
  );
};

export default OffersTable;
